import { z } from 'zod';

export const typedInputEncodingSchema = z.enum(['galapagos_typed_v1']);
export type TypedInputEncoding = z.infer<typeof typedInputEncodingSchema>;
export const TYPED_INPUT_ENCODING = typedInputEncodingSchema.enum.galapagos_typed_v1;

const MEMBER_OPEN = '<galapagos_typed_member_message_v1>\n';
const MEMBER_CLOSE = '\n</galapagos_typed_member_message_v1>';
const CONTEXT_OPEN = '<previous_agent_message_in_full>\n';
const CONTEXT_CLOSE = '\n</previous_agent_message_in_full>\n\n';

/** A streamed envelope must reach its final chunk before it can be decoded or reconciled. */
export function hasTypedInputPrefix(value: string): boolean {
  return [MEMBER_OPEN, CONTEXT_OPEN].some(
    (prefix) => prefix.startsWith(value) || value.startsWith(prefix),
  );
}

/** Append context as input, without synthesizing assistant tool calls or editing model history. */
export function encodeTypedInput(text: string, previousAgentMessage?: string): string {
  const context =
    previousAgentMessage === undefined
      ? ''
      : `${CONTEXT_OPEN}${previousAgentMessage}${CONTEXT_CLOSE}`;
  return `${context}${MEMBER_OPEN}${JSON.stringify({ text })}${MEMBER_CLOSE}`;
}

/** Decode one exact wire envelope. Ordinary text and malformed envelopes are retained verbatim. */
export function decodeTypedInput(value: string): string {
  let member = value;
  if (member.startsWith(CONTEXT_OPEN)) {
    const boundary = member.lastIndexOf(`${CONTEXT_CLOSE}${MEMBER_OPEN}`);
    if (boundary === -1) return value;
    member = member.slice(boundary + CONTEXT_CLOSE.length);
  }
  if (!member.startsWith(MEMBER_OPEN) || !member.endsWith(MEMBER_CLOSE)) return value;
  try {
    const body: unknown = JSON.parse(member.slice(MEMBER_OPEN.length, -MEMBER_CLOSE.length));
    if (
      body !== null &&
      typeof body === 'object' &&
      Object.keys(body).length === 1 &&
      'text' in body &&
      typeof body.text === 'string'
    )
      return body.text;
  } catch {
    // A member's ordinary text may resemble markup; it is not discarded on parse failure.
  }
  return value;
}

/**
 * Largest typed turn the voice agent worker accepts, in UTF-8 bytes of the
 * trimmed text it receives. The worker owns the real cap and drops anything
 * larger without telling the client; this mirrors it so the app can refuse or
 * shrink a turn before sending. Change both together.
 */
export const TYPED_INPUT_MAX_BYTES = 4096;

/** ASCII only: it is rendered into the agent's input, which must carry no banned dash. */
const TRUNCATION_MARKER = '[Earlier part of this message omitted] ...\n';

/** UTF-8 length without a platform encoder; a lone surrogate counts as U+FFFD, as an encoder writes it. */
function utf8ByteLength(value: string): number {
  let bytes = 0;
  for (let index = 0; index < value.length; index += 1) {
    const unit = value.charCodeAt(index);
    if (unit < 0x80) bytes += 1;
    else if (unit < 0x800) bytes += 2;
    else if (unit >= 0xd800 && unit <= 0xdbff) {
      const next = value.charCodeAt(index + 1);
      if (next >= 0xdc00 && next <= 0xdfff) {
        bytes += 4;
        index += 1;
      } else bytes += 3;
    } else bytes += 3;
  }
  return bytes;
}

/** Size of the exact frame text the worker measures for this typed turn. */
export function typedInputByteLength(text: string, previousAgentMessage?: string): number {
  return utf8ByteLength(encodeTypedInput(text, previousAgentMessage).trim());
}

export type TypedInputFit =
  | { ok: true; context: 'none' | 'full'; message: string }
  | {
      ok: true;
      context: 'truncated' | 'dropped';
      message: string;
      contextBytes: number;
      keptContextBytes: number;
    }
  | { ok: false; memberBytes: number; limitBytes: number };

/**
 * Fit a typed turn under the worker's cap. The member's own words are never
 * shortened: if they cannot fit the turn is refused. When only the quoted
 * previous reply overflows, its end is kept (it usually holds what the member
 * is answering) behind a fixed marker, cut on a whole code point.
 */
export function fitTypedInput(
  text: string,
  previousAgentMessage?: string,
  limitBytes: number = TYPED_INPUT_MAX_BYTES,
): TypedInputFit {
  const memberOnly = encodeTypedInput(text);
  const memberBytes = utf8ByteLength(memberOnly.trim());
  if (memberBytes > limitBytes) return { ok: false, memberBytes, limitBytes };
  if (previousAgentMessage === undefined) return { ok: true, context: 'none', message: memberOnly };
  const full = encodeTypedInput(text, previousAgentMessage);
  if (utf8ByteLength(full.trim()) <= limitBytes)
    return { ok: true, context: 'full', message: full };

  const contextBytes = utf8ByteLength(previousAgentMessage);
  const budget = limitBytes - utf8ByteLength(encodeTypedInput(text, TRUNCATION_MARKER).trim());
  const codePoints = Array.from(previousAgentMessage);
  let kept = 0;
  let keptBytes = 0;
  while (kept < codePoints.length) {
    const next = utf8ByteLength(codePoints[codePoints.length - 1 - kept] ?? '');
    if (keptBytes + next > budget) break;
    keptBytes += next;
    kept += 1;
  }
  if (kept === 0)
    return { ok: true, context: 'dropped', message: memberOnly, contextBytes, keptContextBytes: 0 };
  const suffix = codePoints.slice(codePoints.length - kept).join('');
  return {
    ok: true,
    context: 'truncated',
    message: encodeTypedInput(text, `${TRUNCATION_MARKER}${suffix}`),
    contextBytes,
    keptContextBytes: keptBytes,
  };
}
